import * as pdfjsLib from 'pdfjs-dist';
// Vite resolves this to a served URL for the pdf.js web worker (ESM build).
// pdf.js runs its parser off the main thread; without a workerSrc it throws.
import PdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

import {
  isLayoutBlockType,
  stepsWithPdfTemplates,
  type PdfFieldMapping,
  type WorkflowTemplate,
} from '@/api/workflows';

pdfjsLib.GlobalWorkerOptions.workerSrc = PdfWorkerUrl;

export { pdfjsLib };

/**
 * Fields common to every mappable row — the full column path so the save
 * endpoint can address it plus the breadcrumb label.
 */
type MappableBase = {
  /**
   * Unique per row. `fieldId` for a scalar field; `fieldId:columnId:rowIndex` for a
   * repeatable-row column; a `#instanceIndex` suffix is added for fields/options that
   * live in a repeatable section.
   */
  key: string;
  fieldId: string;
  stepId: string;
  sectionId: string;
  rowId: string;
  columnId: string;
  /** Breadcrumb label (step › section › field[ › column]) in the active language. */
  label: string;
  /**
   * When this row belongs to a repeatable group (a RepeatableRow field, or a field
   * inside a repeatable section), the stable id of that group — the mapping page folds
   * all rows sharing a `groupKey` under one collapsible header. Undefined for
   * standalone (non-repeatable) rows, which render exactly as before.
   */
  groupKey?: string;
  /** Breadcrumb header shown on the collapsible group (step › section[ › field]). */
  groupLabel?: string;
  /** 1-based index within the group (repeatable-section instance, or repeatable row). */
  instanceIndex?: number;
};

/** No hard limit on a repeatable element → cap the generated rows at this many. */
export const DEFAULT_REPEATABLE_LIMIT = 10;

/**
 * One row in the PDF-mapping page's "form fields" list. A scalar field carries a
 * list of per-PDF `mappings` (1-to-many across documents); a choice option row
 * carries the same list but toggles a checkbox mapping keyed by its option value;
 * a single RepeatableRow column keeps the scalar 1:1 `internalName` handle
 * (document-agnostic).
 */
export type MappableField =
  | (MappableBase & { kind: 'field'; mappings: PdfFieldMapping[] })
  | (MappableBase & {
      kind: 'option';
      /** The option's value within the parent choice field. */
      optionValue: string;
      mappings: PdfFieldMapping[];
    })
  | (MappableBase & {
      kind: 'column';
      /** Addresses one column inside the parent RepeatableRow's template. */
      repeatableColumnId: string;
      /** 1-based repeatable row this entry maps (each row maps independently). */
      rowIndex: number;
      /** Current AcroForm mapping handle for this row, or null when unmapped. */
      internalName: string | null;
    });

const localized = (en: string, fr: string, isFr: boolean) => (isFr ? fr : en) || en || fr;

/**
 * Walk every DynamicForm step and flatten it into mappable rows. Mirrors the
 * steps → sections → rows → columns → field walk used by
 * {@link collectFieldChoices}, but keeps the full column path (needed to address
 * the save endpoint), skips layout blocks (nothing to map), and expands
 * RepeatableRow fields into one row per template column instead of the parent.
 */
export function collectMappableFields(template: WorkflowTemplate, isFr: boolean): MappableField[] {
  const out: MappableField[] = [];
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    const stepLabel = localized(step.titleEn, step.titleFr, isFr);
    for (const section of step.sections) {
      const sectionLabel = localized(section.titleEn, section.titleFr, isFr);
      // A repeatable section expands each of its fields/options into one
      // independently-mappable row per instance (1..limit); a non-repeatable
      // section keeps a single row per field, exactly as before.
      const sectionRepeatable = section.repeatable === true;
      const sectionLimit = sectionRepeatable
        ? (section.maxInstances ?? DEFAULT_REPEATABLE_LIMIT)
        : 1;
      const instanceIndices: (number | undefined)[] = sectionRepeatable
        ? Array.from({ length: sectionLimit }, (_, i) => i + 1)
        : [undefined];
      const sectionGroupKey = sectionRepeatable ? section.id : undefined;
      const sectionGroupLabel = sectionRepeatable ? `${stepLabel} › ${sectionLabel}` : undefined;

      for (const row of section.rows) {
        for (const column of row.columns) {
          const f = column.field;
          if (!f) continue;
          if (isLayoutBlockType(f.type)) continue;
          const fieldLabel = localized(f.labelEn, f.labelFr, isFr);
          const base = {
            fieldId: f.id,
            stepId: step.id,
            sectionId: section.id,
            rowId: row.id,
            columnId: column.id,
          };

          if (
            f.type === 'RepeatableRow' &&
            f.repeatableTemplate &&
            f.repeatableTemplate.length > 0
          ) {
            // The parent row itself isn't an input — each column carries its own
            // scalar AcroForm handle. Rather than an implicit {N} iterator, expand
            // one independently-mappable row per index up to the limit. Columns are
            // document-agnostic 1:1, so they're not multiplied by a section instance.
            const rowLimit = f.maxRows ?? DEFAULT_REPEATABLE_LIMIT;
            const groupLabel = `${stepLabel} › ${sectionLabel} › ${fieldLabel}`;
            for (const col of f.repeatableTemplate) {
              const colLabel = localized(col.labelEn, col.labelFr, isFr);
              for (let i = 1; i <= rowLimit; i++) {
                out.push({
                  ...base,
                  kind: 'column',
                  key: `${f.id}:${col.id}:${i}`,
                  repeatableColumnId: col.id,
                  rowIndex: i,
                  groupKey: f.id,
                  groupLabel,
                  instanceIndex: i,
                  label: `${groupLabel} › ${colLabel}`,
                  internalName:
                    col.mappings?.find((m) => m.rowIndex === i)?.internalName ??
                    // Legacy single handle is only a read hint for the first row.
                    (i === 1 ? (col.internalName ?? null) : null),
                });
              }
            }
            continue;
          }

          for (const inst of instanceIndices) {
            const suffix = inst === undefined ? '' : `#${inst}`;
            out.push({
              ...base,
              kind: 'field',
              key: `${f.id}${suffix}`,
              groupKey: sectionGroupKey,
              groupLabel: sectionGroupLabel,
              instanceIndex: inst,
              label: `${stepLabel} › ${sectionLabel} › ${fieldLabel}`,
              mappings: (f.pdfMappings ?? []).filter(
                (m) => (m.instanceIndex ?? undefined) === inst,
              ),
            });
            // Choice types additionally expose each option as its own mappable row
            // — an option maps to a PDF checkbox widget that's CHECKED when picked.
            if (
              f.type === 'SingleSelect' ||
              f.type === 'RadioGroup' ||
              f.type === 'CheckboxGroup'
            ) {
              for (const opt of f.options ?? []) {
                const optLabel = localized(opt.labelEn, opt.labelFr, isFr) || opt.value;
                out.push({
                  ...base,
                  kind: 'option',
                  key: `${f.id}::opt::${opt.value}${suffix}`,
                  optionValue: opt.value,
                  groupKey: sectionGroupKey,
                  groupLabel: sectionGroupLabel,
                  instanceIndex: inst,
                  label: `${stepLabel} › ${sectionLabel} › ${fieldLabel} › ${optLabel}`,
                  mappings: (opt.pdfMappings ?? []).filter(
                    (m) => (m.instanceIndex ?? undefined) === inst,
                  ),
                });
              }
            }
          }
        }
      }
    }
  }
  return out;
}

/** Flatten every PDF source template into a picker list for the viewer. */
export type PdfEntryOption = {
  id: string;
  label: string;
  enFileKey: string | null;
  frFileKey: string | null;
};

export function collectPdfEntryOptions(template: WorkflowTemplate): PdfEntryOption[] {
  const out: PdfEntryOption[] = [];
  for (const step of stepsWithPdfTemplates(template)) {
    for (const e of step.fileEntries) {
      out.push({
        id: e.id,
        label: e.label || e.id,
        enFileKey: e.enFileKey ?? null,
        frFileKey: e.frFileKey ?? null,
      });
    }
  }
  return out;
}
